import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ExternalLink } from 'lucide-react'
import { getOpenBankingLink } from '../api/bank-accounts-api'
import type { CountryWithInstitutions } from '../lib/countries-with-institutions'
import { WizardInstitutionList } from './wizard-institution-list'

interface WizardOpenBankingPanelProps {
  country: CountryWithInstitutions
  companyId: number
  search: string
  onBusyChange: (busy: boolean) => void
}

/** Open Banking bank list for the selected country. The search and company
 *  selector live in the configure step's toolbar above (companyId/search props). */
export function WizardOpenBankingPanel({
  country, companyId, search, onBusyChange,
}: WizardOpenBankingPanelProps) {
  const linkMutation = useMutation({
    mutationFn: (institutionId: number) => getOpenBankingLink(institutionId, companyId),
    onSuccess: (data) => {
      const url = data.redirectUrl ?? data.url ?? data.Link
      if (url) {
        window.location.href = url
      } else {
        toast.error('No redirect URL returned from bank')
      }
    },
    onError: () => toast.error('Failed to initiate bank connection'),
  })

  useEffect(() => {
    onBusyChange(linkMutation.isPending)
    return () => onBusyChange(false)
  }, [linkMutation.isPending, onBusyChange])

  function handleInstitutionClick(institutionId: number) {
    if (!companyId) {
      toast.error('Please select a company first')
      return
    }
    linkMutation.mutate(institutionId)
  }

  return (
    <WizardInstitutionList
      institutions={country.Institutions}
      search={search}
      onSelect={handleInstitutionClick}
      disabled={!companyId || linkMutation.isPending}
      trailingIcon={<ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />}
      emptyMessage="No institutions found in this country."
      footer={!companyId && (
        <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-2">
          Select a company above before choosing a bank.
        </p>
      )}
    />
  )
}
